import React, { useEffect, useRef, useMemo } from 'react';
import { Container } from "react-bootstrap";
import {useDispatch, useSelector, shallowEqual} from "react-redux";
import {doQuery, setQuery} from "../services/Query_Slice";
import { cursorsType, setCursors, selectDateIntervalFilterQuery, selectCursors, selectSortConfig, selectCalendarRange, selectViewType, selectAutoNav, selectDateInterval, selectPageRange, calRangeType, viewType as vt } from 'services/View_Slice';
import {RootState} from "../services/rootReducer";
import {buildQueryString} from 'utils/solr';
import { buildQueryViewParams } from './utils/queryHelper';
import styles from "./css/Query.module.css";
import { getSummaryShort } from 'services/Summary_Slice';
import { getGradientMap } from 'services/View_Slice';
import CONSTANTS from "../constants";
import QueryDebugPanel from './QueryDebugPanel';

function Query() {
    const {facetMap, facetSelected} = useSelector(
        (state: RootState) => ({
            facetMap: state.Facets.facetMap,
            facetSelected: state.Facets.facetSelected,
        }),
        shallowEqual
    );

    const freeTextSearch = useSelector((state: RootState) => state.Search.freeTextSearch);

    // Granular selectors — each only triggers re-render when its specific fields change
    const viewType = useSelector(selectViewType);
    const { cursorStart, cursorRows } = useSelector(selectCursors);
    const { listSortedFeld, listSortedOrder } = useSelector(selectSortConfig);
    const { calRangeUsed, calRangeValue } = useSelector(selectCalendarRange);
    const { dateIntervalFrom, dateIntervalTo } = useSelector(selectDateInterval);
    const isAutoNavigating = useSelector(selectAutoNav);
    const pageRangeUsed = useSelector(selectPageRange);
    const urlConstraintsUsed = useSelector((state: RootState) => state.View.urlConstraintsUsed);

    // Single selector for debug flag — only triggers rerender when debug mode toggles
    const showDebugInfo = useSelector((state: RootState) => state.View.showDebugInfo);

    // Use memoized selector for date interval filter query string
    const dateIntervalFilterQuery = useSelector(selectDateIntervalFilterQuery);


    // use effect to:
    // - set query string in store
    // - load summary
    // - CENTRALIZED: dispatch queries for ALL list view state changes
    const dispatch = useDispatch();

    // Track previous filter values for pagination reset logic
    const prevFiltersRef = useRef({ freeTextSearch, facetSelected, dateIntervalFrom, dateIntervalTo });

    // Detect filter changes and reset pagination when filters change
    useEffect(() => {
        const filtersChanged =
            prevFiltersRef.current.freeTextSearch !== freeTextSearch ||
            JSON.stringify(prevFiltersRef.current.facetSelected) !== JSON.stringify(facetSelected) ||
            prevFiltersRef.current.dateIntervalFrom !== dateIntervalFrom ||
            prevFiltersRef.current.dateIntervalTo !== dateIntervalTo;

        if (filtersChanged && cursorStart !== 0) {
            // Filters changed but we're not on page 0 - reset pagination
            const cursorInfo: cursorsType = {cursorStart: 0, cursorRows: pageRangeUsed};
            dispatch(setCursors(cursorInfo));
        }

        prevFiltersRef.current = { freeTextSearch, facetSelected, dateIntervalFrom, dateIntervalTo };
    }, [freeTextSearch, facetSelected, dateIntervalFrom, dateIntervalTo, cursorStart, pageRangeUsed, dispatch]);

    // Memoize the base query string — only recalculates when search/facet inputs change
    const baseQuery = useMemo(() => {
        if ('loading' in facetMap) return null;
        return buildQueryString('*:*', freeTextSearch, undefined, undefined, facetSelected, facetMap, CONSTANTS.DEFAULT_OPERATOR_TO_USE);
    }, [freeTextSearch, facetSelected, facetMap]);

    // Main query effect - watches ALL list view state
    useEffect(() => {
            // Skip during auto-navigation - final call will come when navigation completes
            if (isAutoNavigating) return;

            // if we miss some essential data (if some api call has failed), don't break the UI but display notification
            if(baseQuery === null){
                // facetMap not loaded yet
            }else{
                dispatch(setQuery(baseQuery));
                dispatch(getSummaryShort(facetMap, facetSelected, freeTextSearch, CONSTANTS.DEFAULT_OPERATOR_TO_USE, dateIntervalFrom ?? undefined, dateIntervalTo ?? undefined)) /* === AND/OR OPERATOR FEATURE - USING DEFAULT CONSTANT === */
                dispatch(getGradientMap(freeTextSearch, facetSelected, CONSTANTS.DEFAULT_OPERATOR_TO_USE, dateIntervalFrom ?? undefined, dateIntervalTo ?? undefined)); /* === AND/OR OPERATOR FEATURE - USING DEFAULT CONSTANT === */

                // ONLY dispatch for list view (calendar view uses DateNav dispatches)
                if (viewType === 0) {
                    // Build complete query with ALL current state
                    const hasSearchText = !!(freeTextSearch && freeTextSearch.trim().length > 0);
                    const viewparams = buildQueryViewParams(viewType, cursorStart, cursorRows, listSortedFeld, listSortedOrder, calRangeUsed, calRangeValue, undefined, hasSearchText);

                    // Use centralized selector for date interval filter
                    const dateIntervalFq = dateIntervalFilterQuery;

                    const completeQuery = `${baseQuery}&${viewparams}${dateIntervalFq}`;

                    dispatch(doQuery(completeQuery));
                }
            }
    }, [dispatch, isAutoNavigating, baseQuery, freeTextSearch, facetSelected, facetMap, viewType,
        cursorStart, cursorRows,
        listSortedFeld, listSortedOrder,
        calRangeUsed, calRangeValue,
        dateIntervalFilterQuery]);
    // CENTRALIZED QUERY ARCHITECTURE:
    // This useEffect is the ONLY place that dispatches doQuery() for list view
    // All other components update Redux state only
    // Dependencies include ALL state that should trigger queries:
    // - Search: freeTextSearch
    // - Facets: facetSelected, facetMap
    // - Pagination: cursorStart, cursorRows, pageRangeUsed
    // - Sorting: listSortedFeld, listSortedOrder
    // - Calendar navigation: calRangeUsed, calRangeValue
    // - Date interval filter: dateIntervalFrom, dateIntervalTo
    // - View type: viewType (list vs calendar)

    // The calendar week view shows the docs of the last query, which DateNav/FacetNavigation
    // only fetch when the user navigates. When the app opens directly in week view (shared
    // link or reload), fetch the week once, the same way DateNav does, after the URL is applied.
    // Like DateNav, keep the week's date filter when there is search text (baseQuery already
    // holds the text) and leave out the date interval, which the calendar views don't apply.
    const initialWeekQueryDoneRef = useRef(false);
    useEffect(() => {
        if (initialWeekQueryDoneRef.current || !urlConstraintsUsed || isAutoNavigating || baseQuery === null) return;
        initialWeekQueryDoneRef.current = true;
        if (viewType === vt.Calendar && calRangeUsed === calRangeType.week) {
            const viewparams = buildQueryViewParams(viewType, 0, pageRangeUsed, listSortedFeld, listSortedOrder, calRangeUsed, calRangeValue, undefined, false);
            dispatch(doQuery(baseQuery + "&" + viewparams));
        }
    }, [dispatch, urlConstraintsUsed, isAutoNavigating, baseQuery, viewType,
        pageRangeUsed, listSortedFeld, listSortedOrder, calRangeUsed, calRangeValue]);


    return (
        <Container className={styles.container}>
            {showDebugInfo === true ? <QueryDebugPanel /> : <span></span>}
        </Container>
    );
}

export default Query;
